import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Spin, Button, Tooltip } from "antd";
import {
  LeftOutlined,
  RightOutlined,
  AppstoreOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import dayjs from "dayjs";
import {
  getCalendar,
  rescheduleTask,
  rescheduleSiteVisit,
} from "../../../services/features/dashboard.service";
import { hexToRgba } from "../../../services/helpFunctions/hexToRgba";
import StatusPill from "../../components/StatusPill/StatusPill";
import MessagePopUp from "../../components/Messages/MessagePopUp";
import doneIcon from "../../../Assets/svg/done.svg";
import errorIcon from "../../../Assets/svg/exclamationMark.svg";
import Preview from "../planning/VisitBoard/TableView/modals/Preview";
import "./PlanningCalendar.scss";

// F-06 — unified planning calendar. Plots site visits, tasks and logistics,
// colour-coded by type. Site visits and tasks can be dragged onto another day
// to reschedule them (the same interaction as the kanban board); logistics have
// no reschedule endpoint, so they render as static markers. Clicking a site
// visit opens its full read-only Preview.
const TYPE_META = {
  SiteVisit: { color: "#AA1A5F", label: "Site visit" },
  Task: { color: "#3B81FE", label: "Task" },
  Logistic: { color: "#FC9403", label: "Logistic" },
};
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DRAGGABLE_TYPES = ["SiteVisit", "Task"];
const dayKey = (d) => d.format("YYYY-MM-DD");

const PlanningCalendar = () => {
  const projectID = localStorage.getItem("currentProjectID");
  const [cursor, setCursor] = useState(dayjs()); // month being viewed
  const [view, setView] = useState("month"); // "month" | "year"
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const [msg, setMsg] = useState({
    trigger: false,
    type: "",
    icon: "",
    title: "",
    text: "",
  });

  const year = cursor.year();

  const notify = (type, title, text) => {
    setMsg({
      trigger: true,
      type,
      icon: type === "success" ? doneIcon : errorIcon,
      title,
      text,
    });
    setTimeout(() => setMsg((m) => ({ ...m, trigger: false })), 3000);
  };

  // Load the whole visible year at once so paging between months needs no
  // extra round-trips. Reloads only when the year (or project) changes.
  const load = useCallback(() => {
    if (!projectID) return;
    const ref = dayjs().year(year);
    const from = ref.startOf("year").startOf("day").toISOString();
    const to = ref.endOf("year").endOf("day").toISOString();
    setLoading(true);
    getCalendar(
      { projectId: projectID, from, to },
      (res) => {
        const list = res?.data || res || [];
        setEvents(Array.isArray(list) ? list : []);
        setLoading(false);
      },
      () => setLoading(false)
    );
  }, [projectID, year]);

  useEffect(() => {
    load();
  }, [load]);

  const byDay = useMemo(() => {
    const map = new Map();
    events.forEach((e) => {
      if (!e?.date) return;
      const key = dayjs(e.date).format("YYYY-MM-DD");
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    });
    return map;
  }, [events]);

  const byMonth = useMemo(() => {
    const map = new Map();
    events.forEach((e) => {
      if (!e?.date) return;
      const d = dayjs(e.date);
      if (d.year() !== year) return;
      const m = d.month();
      if (!map.has(m)) map.set(m, {});
      const bucket = map.get(m);
      bucket[e.type] = (bucket[e.type] || 0) + 1;
    });
    return map;
  }, [events, year]);

  // The 42 cells (6 weeks) covering the visible month.
  const gridDays = useMemo(() => {
    const start = cursor.startOf("month").startOf("week");
    return Array.from({ length: 42 }, (_, i) => start.add(i, "day"));
  }, [cursor]);

  const openPreview = (event) => {
    const id = event.siteVisitId || event.id;
    if (id) setPreviewId(id);
  };

  // Drag a visit/task onto another day → reschedule it.
  const onDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination || destination.droppableId === source.droppableId) return;

    const [type] = draggableId.split("::");
    const evId = draggableId.slice(type.length + 2);
    const moved = events.find((e) => e.type === type && String(e.id) === evId);
    if (!moved) return;

    const orig = dayjs(moved.date);
    const dest = dayjs(destination.droppableId)
      .hour(orig.hour())
      .minute(orig.minute())
      .second(0);
    const newDateIso = dest.toISOString();

    // Optimistic move; revert by reloading if the server rejects.
    const prevEvents = events;
    setEvents((list) =>
      list.map((e) =>
        e.type === type && String(e.id) === evId ? { ...e, date: newDateIso } : e
      )
    );
    setSaving(true);

    const onOk = () => {
      setSaving(false);
      notify(
        "success",
        "Rescheduled",
        `"${moved.title || "Item"}" moved to ${dest.format("MMM D, YYYY")}.`
      );
    };
    const onErr = () => {
      setSaving(false);
      setEvents(prevEvents);
      notify("danger", "Couldn't reschedule", "The change was not saved.");
    };

    if (type === "SiteVisit") {
      rescheduleSiteVisit(moved.siteVisitId || moved.id, newDateIso, onOk, onErr);
    } else if (type === "Task") {
      rescheduleTask(moved.id, newDateIso, onOk, onErr);
    } else {
      setSaving(false);
    }
  };

  const goToday = () => setCursor(dayjs());
  const goPrev = () =>
    setCursor((c) => (view === "year" ? c.subtract(1, "year") : c.subtract(1, "month")));
  const goNext = () =>
    setCursor((c) => (view === "year" ? c.add(1, "year") : c.add(1, "month")));

  const renderEventPill = (e, draggable, index) => {
    const meta = TYPE_META[e.type] || { color: "#707073" };
    const clickable = e.type === "SiteVisit" && (e.siteVisitId || e.id);
    const styleFor = (dragging) => ({
      color: meta.color,
      background: hexToRgba(meta.color, dragging ? 0.22 : 0.12),
      border: `1px solid ${hexToRgba(meta.color, 0.35)}`,
      cursor: draggable ? "grab" : clickable ? "pointer" : "default",
    });
    const content = (
      <>
        <span className="calEventDot" style={{ background: meta.color }} />
        <span className="calEventText">{e.title}</span>
      </>
    );

    if (!draggable) {
      return (
        <li
          key={`${e.type}-${e.id}`}
          className="calEventPill"
          title={e.title}
          onClick={clickable ? () => openPreview(e) : undefined}
          style={styleFor(false)}
        >
          {content}
        </li>
      );
    }

    return (
      <Draggable
        key={`${e.type}-${e.id}`}
        draggableId={`${e.type}::${e.id}`}
        index={index}
      >
        {(provided, snapshot) => (
          <li
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className="calEventPill"
            title={e.title}
            onClick={clickable ? () => openPreview(e) : undefined}
            style={{ ...styleFor(snapshot.isDragging), ...provided.draggableProps.style }}
          >
            {content}
          </li>
        )}
      </Draggable>
    );
  };

  const renderMonthGrid = () => (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="calGrid">
        {WEEKDAYS.map((w) => (
          <div key={w} className="calWeekday">
            {w}
          </div>
        ))}
        {gridDays.map((day) => {
          const key = dayKey(day);
          const items = byDay.get(key) || [];
          const inMonth = day.month() === cursor.month();
          const isToday = day.isSame(dayjs(), "day");
          // Draggable index must be contiguous per droppable, counting only the
          // draggable (visit/task) items.
          let dragIndex = -1;
          return (
            <Droppable droppableId={key} key={key}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`calCell ${inMonth ? "" : "calCell--muted"} ${
                    snapshot.isDraggingOver ? "calCell--over" : ""
                  }`}
                >
                  <div className={`calCellDate ${isToday ? "calCellDate--today" : ""}`}>
                    {day.date()}
                  </div>
                  <ul className="calEvents">
                    {items.map((e) => {
                      const draggable = DRAGGABLE_TYPES.includes(e.type);
                      if (draggable) dragIndex += 1;
                      return renderEventPill(e, draggable, draggable ? dragIndex : 0);
                    })}
                    {provided.placeholder}
                  </ul>
                </div>
              )}
            </Droppable>
          );
        })}
      </div>
    </DragDropContext>
  );

  const renderYearGrid = () => (
    <div className="calYearGrid">
      {Array.from({ length: 12 }, (_, m) => {
        const bucket = byMonth.get(m) || {};
        const monthDate = dayjs().year(year).month(m);
        return (
          <div
            key={m}
            className="calYearCell"
            onClick={() => {
              setCursor(monthDate);
              setView("month");
            }}
          >
            <div className="calYearMonth">{monthDate.format("MMMM")}</div>
            <div className="calYearCounts">
              {Object.entries(TYPE_META).map(([type, meta]) => {
                const count = bucket[type];
                if (!count) return null;
                return (
                  <span
                    key={type}
                    className="calYearCount"
                    style={{
                      color: meta.color,
                      background: hexToRgba(meta.color, 0.12),
                      border: `1px solid ${hexToRgba(meta.color, 0.35)}`,
                    }}
                  >
                    <span
                      className="calEventDot"
                      style={{ background: meta.color }}
                    />
                    {count}
                  </span>
                );
              })}
              {Object.keys(bucket).length === 0 && (
                <span className="calYearEmpty">—</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="page-content planningCalendar" style={{ padding: 24 }}>
      <MessagePopUp
        trigger={msg.trigger}
        type={msg.type}
        icon={msg.icon}
        messageTitle={msg.title}
        messageText={msg.text}
      />


      <div className="calToolbar">
        <div className="calNav">
          <Button
            icon={<LeftOutlined />}
            onClick={goPrev}
            aria-label="Previous"
          />
          <Button onClick={goToday}>Today</Button>
          <Button
            icon={<RightOutlined />}
            onClick={goNext}
            aria-label="Next"
          />
          <span className="calTitle">
            {view === "year" ? year : cursor.format("MMMM YYYY")}
          </span>
          {(loading || saving) && <Spin size="small" />}
        </div>

        <div className="calRight">
          <div className="calViewToggle">
            <Tooltip title="Month view">
              <Button
                type={view === "month" ? "primary" : "default"}
                icon={<CalendarOutlined />}
                onClick={() => setView("month")}
              >
                Month
              </Button>
            </Tooltip>
            <Tooltip title="Year overview">
              <Button
                type={view === "year" ? "primary" : "default"}
                icon={<AppstoreOutlined />}
                onClick={() => setView("year")}
              >
                Year
              </Button>
            </Tooltip>
          </div>
          <div className="calLegend">
            {Object.entries(TYPE_META).map(([k, m]) => (
              <StatusPill key={k} text={m.label} fill={m.color} />
            ))}
          </div>
        </div>
      </div>

      {view === "month" && (
        <p className="calHint">Drag a site visit or task to another day to reschedule it.</p>
      )}

      <div className="calScroll">
        {view === "month" ? renderMonthGrid() : renderYearGrid()}
      </div>

      <Preview
        visible={!!previewId}
        onClose={() => setPreviewId(null)}
        siteVisitCardId={previewId}
      />
    </div>
  );
};

export default PlanningCalendar;
